import { ClassDefinition } from '../components/ClassEditor';
import { ClassMap, ListKey, ResolvedLists } from './class-map';

export type { ListKey, ResolvedLists };

export const LIST_KEYS: ListKey[] = ['toolbar', 'financial-toolbar', 'page', 'table'];

export interface ListRow {
  key: string;
  label: string;
  className: string | null;
  kind: 'class' | 'element';
}

export interface ClassListGroup {
  list: ListKey;
  rows: ListRow[];
}

export interface GroupedClasses {
  groups: ClassListGroup[];
  ungrouped: string[];
}

export function classNameOf(key: string, list: ListKey): string | null {
  if (list === 'page' || list === 'table') {
    return key;
  }
  const dot = key.indexOf('.');
  return dot < 0 ? null : key.slice(dot + 1);
}

export function buildGroups(classMap: ClassMap, lists: ResolvedLists): GroupedClasses {
  const listed = new Set<string>();

  const groups = LIST_KEYS.map((list) => ({
    list,
    rows: Object.entries(lists[list] ?? {}).map(([key, label]) => {
      const className = classNameOf(key, list);
      const backed = className !== null && className in classMap;
      if (backed) listed.add(className as string);
      return { key, label, className: backed ? className : null, kind: backed ? ('class' as const) : ('element' as const) };
    }),
  }));

  const ungrouped = Object.keys(classMap).filter((name) => !listed.has(name));

  return { groups, ungrouped };
}

const rebuild = (order: string[], entries: Record<string, string>): Record<string, string> =>
  order.reduce<Record<string, string>>((acc, key) => {
    acc[key] = entries[key];
    return acc;
  }, {});

export function reorderEntries(entries: Record<string, string>, order: string[]): Record<string, string> {
  const known = new Set(Object.keys(entries ?? {}));
  const dropped = order.filter((key) => known.has(key));
  const missing = Object.keys(entries ?? {}).filter((key) => !dropped.includes(key));

  return rebuild([...dropped, ...missing], entries);
}

export function insertAfter(entries: Record<string, string>, afterKey: string, key: string, label: string): Record<string, string> {
  const order = Object.keys(entries ?? {}).filter((existing) => existing !== key);
  const at = order.indexOf(afterKey);
  order.splice(at < 0 ? order.length : at + 1, 0, key);

  return rebuild(order, { ...entries, [key]: label });
}

export const CLONE_NAME_SUFFIX = 'copy';
export const CLONE_LABEL_SUFFIX = '(copy)';

const suffixLabel = (label: string): string => (label.trim() ? `${label} ${CLONE_LABEL_SUFFIX}` : label);

// The modal blocks duplicate names, so the prefill has to be free on arrival.
const freeName = (base: string, taken: string[]): string => {
  const used = new Set(taken);
  const first = `${base}-${CLONE_NAME_SUFFIX}`;
  if (!used.has(first)) {
    return first;
  }
  let n = 2;
  while (used.has(`${first}-${n}`)) {
    n += 1;
  }
  return `${first}-${n}`;
};

export function cloneDefinition(sourceName: string, source: ClassDefinition, existingNames: string[]): { name: string; def: ClassDefinition } {
  return {
    name: freeName(sourceName, existingNames),
    def: {
      ...source,
      elements: [...source.elements],
      properties: { ...source.properties },
      labelSv: suffixLabel(source.labelSv),
      labelEn: suffixLabel(source.labelEn),
      ...(source.toolbars ? { toolbars: [...source.toolbars] } : {}),
      ...(source.scopes ? { scopes: [...source.scopes] } : {}),
    },
  };
}

export function labelIsTaken(classMap: ClassMap, label: string, selfName: string): string | null {
  const needle = label.trim().toLowerCase();
  if (!needle) {
    return null;
  }
  for (const [name, def] of Object.entries(classMap)) {
    if (name === selfName) continue;
    if (def.labelSv.trim().toLowerCase() === needle || def.labelEn.trim().toLowerCase() === needle) {
      return name;
    }
  }
  return null;
}

export function placeCloneAfterSource(lists: ResolvedLists, cloneName: string, sourceName: string): ResolvedLists {
  const out: ResolvedLists = { ...lists };

  for (const list of LIST_KEYS) {
    const keysFor = (name: string) => Object.keys(out[list] ?? {}).filter((key) => classNameOf(key, list) === name);
    const cloneKeys = keysFor(cloneName);
    const sourceKeys = keysFor(sourceName);
    if (cloneKeys.length === 0 || sourceKeys.length === 0) {
      continue;
    }

    let after = sourceKeys[sourceKeys.length - 1];
    let entries = out[list];
    for (const key of cloneKeys) {
      entries = insertAfter(entries, after, key, entries[key]);
      after = key;
    }
    out[list] = entries;
  }

  return out;
}

export function parseEntryLabel(value: string): { sv: string; en: string } {
  const raw = value ?? '';
  if (!raw.trim()) {
    return { sv: '', en: '' };
  }

  const sv = raw.match(/SV:(.*?)(?=\s*EN:|$)/i);
  const en = raw.match(/EN:(.*?)(?=\s*SV:|$)/i);
  if (!sv && !en) {
    const legacy = raw.trim();
    return { sv: legacy, en: legacy };
  }

  return { sv: sv ? sv[1].trim() : '', en: en ? en[1].trim() : '' };
}
